/**
 * Transactions Articles — Spanish
 */

import type { HelpArticle } from "../../../types";

export const TRANSACTIONS_ARTICLES_ES: HelpArticle[] = [
  {
    id: "tx-001",
    slug: "transaction-statuses-explained",
    title: "Explicación de los estados de transacción",
    description: "Entendiendo qué significa cada estado",
    category: "transactions",
    popular: true,
    order: 1,
    keywords: ["estado", "pendiente", "completado", "procesando", "fallido"],
    content: `
# Explicación de los estados de transacción

Cada transacción pasa por varias etapas. Esto es lo que significa cada estado.

## Estados de depósito

### Detectando
- Transacción vista en blockchain
- Esperando inclusión en bloque
- Normalmente muy breve

### Fondos recibidos
- Transacción detectada y confirmada en blockchain
- Los fondos están bajo nuestra custodia
- Esperando confirmaciones requeridas

### Confirmando / Esperando confirmaciones
- Transacción en bloque
- Esperando confirmaciones requeridas (varía según red)
- Progreso mostrado como X/Y confirmaciones
- Ethereum: 12 confirmaciones (~2-5 min)
- Polygon: 128 confirmaciones (~3-8 min)
- Solana: 32 confirmaciones (~15-30 seg)

### Convirtiendo
- Confirmaciones completadas
- Convirtiendo cripto a fiat a tasa de mercado actual
- Proceso automático (no requiere acción)

### Pago enviado
- Conversión completada
- Instrucción de pago enviada al procesador
- En camino a tu banco

### Pago procesado / Liquidado
- Pago procesado por la red bancaria
- Los fondos deberían aparecer en tu cuenta pronto
- Transacción completada

### Completado
- Todos los pasos completados
- Fiat recibido en tu cuenta bancaria
- Transacción finalizada

### Fallido
- Algo salió mal en alguna etapa
- Revisa los detalles del error en la vista de transacción
- Contacta con soporte si es necesario

## Estados de pago

### Pendiente
- En cola para procesamiento
- Esperando verificación de cumplimiento
- Normalmente muy breve

### Procesando
- Siendo preparado para envío
- Cumplimiento aprobado
- Preparando instrucción bancaria

### Enviado
- Enviado al procesador de pagos
- En camino a la red bancaria
- No se puede cancelar

### En tránsito
- En la red bancaria
- Siendo procesado por los bancos
- Casi llegando

### Completado
- Entregado a tu banco
- Debería aparecer en la cuenta
- Puede tardar en reflejarse

### Devuelto
- El banco rechazó la transferencia
- Fondos devueltos al saldo
- Revisa el motivo del error

## Cronología de estados

### Flujo típico de depósito
\`\`\`
Detectando → Fondos recibidos → Esperando confirmaciones → Convirtiendo →
Pago enviado → Pago procesado → Liquidado
   ↓              ↓                    ↓                  ↓
 ~1 min       ~1 min              ~3-10 min          ~1-2 min
                                                           ↓
                                                      ~1-2 min
                                                           ↓
                                                       ¡Listo!
\`\`\`

**Nota:** La interfaz puede mostrar estados simplificados. Haz clic en "Ver detalles" para ver la cronología completa.

### Pago típico (ACH)
\`\`\`
Pendiente → Procesando → Enviado → Completado
   ↓          ↓           ↓           ↓
 ~1 min    ~1-4 horas   ~2-3 días    ¡Listo!
\`\`\`

## Transacciones atascadas

Si el estado no cambia:
- Depósitos: Verifica el explorador de blockchain
- Pagos: Contacta con soporte después de 24h
    `.trim(),
  },
  {
    id: "tx-002",
    slug: "finding-a-transaction",
    title: "Encontrar una transacción",
    description: "Cómo localizar transacciones específicas",
    category: "transactions",
    order: 2,
    keywords: ["encontrar", "buscar", "localizar", "transacción", "historial"],
    content: `
# Encontrar una transacción

Localiza fácilmente cualquier transacción en tu historial.

## Usar búsqueda

### Búsqueda por ID de transacción
1. Ve a Transacciones
2. Haz clic en el icono de búsqueda
3. Introduce el ID de transacción
4. Resultados mostrados al instante

### Búsqueda por rango de fechas
1. Ve a Transacciones
2. Selecciona el filtro de fecha
3. Elige fechas de inicio y fin
4. Ver resultados filtrados

## Opciones de filtro

### Por tipo
- Todas las transacciones
- Solo depósitos
- Solo pagos
- Conversiones

### Por estado
- Todos los estados
- Completado
- Pendiente
- Fallido

### Por monto
- Cualquier monto
- Rango personalizado
- Por encima de umbral
- Por debajo de umbral

### Por activo
- Todos los activos
- Criptomoneda específica
- Moneda fiat específica

## Detalles de transacción

Haz clic en cualquier transacción para ver:
- ID completo de transacción
- Marcas de tiempo
- Detalles del monto
- Historial de estados
- Información de blockchain (depósitos)
- Información bancaria (pagos)

## Opciones de exportación

### Descargar historial
1. Ve a Transacciones
2. Aplica los filtros deseados
3. Haz clic en "Exportar"
4. Elige el formato (CSV, PDF)
5. Descarga el archivo

### Informes fiscales
- Exportaciones de año completo disponibles
- Incluye todos los detalles de transacciones
- Compatible con software fiscal

## Consejos

Usa el ID de transacción para búsqueda más rápida
Establece rango de fechas para acotar resultados
Guarda filtros comunes
Exporta regularmente para tus registros
    `.trim(),
  },
  {
    id: "tx-003",
    slug: "transaction-history",
    title: "Historial de transacciones",
    description: "Ver y gestionar tu historial de transacciones",
    category: "transactions",
    order: 3,
    keywords: ["historial", "registros", "pasado", "ver", "lista"],
    content: `
# Historial de transacciones

Tu registro completo de todas las transacciones en Supabank.

## Acceder al historial

### Panel web
1. Haz clic en "Transacciones" en la barra lateral
2. Ver lista completa de transacciones
3. Desplázate para cargar más

### Móvil
1. Toca "Transacciones" en la navegación inferior
2. Desliza hacia abajo para actualizar
3. Toca una transacción para ver detalles

## Resumen del historial

### Qué incluye
- Todos los depósitos
- Todos los pagos
- Conversiones
- Comisiones aplicadas
- Cambios de estado

### Período de tiempo
- Historial completo retenido
- Sin eliminación automática
- Archivo con búsqueda
- Exportación en cualquier momento

## Tarjetas de transacción

Cada transacción muestra:
- Icono de tipo (depósito/pago)
- Monto y moneda
- Fecha y hora
- Estado actual
- Resumen rápido

## Vista detallada

Haz clic en cualquier transacción para:
- Desglose completo
- Todas las marcas de tiempo
- Números de referencia
- Transacciones relacionadas
- Enlaces de blockchain
- Referencias bancarias
- Desglose de comisiones

## Organizar el historial

### Opciones de ordenación
- Más recientes primero (predeterminado)
- Más antiguos primero
- Monto (mayor a menor)
- Monto (menor a mayor)

### Agrupar por
- Fecha
- Tipo
- Estado
- Moneda

## Retención de datos

- Registros de transacciones: Indefinido
- Datos de blockchain: Permanente
- Referencias bancarias: 7 años
- Registros detallados: 2 años
    `.trim(),
  },
  {
    id: "tx-004",
    slug: "transaction-fees",
    title: "Comisiones de transacción",
    description: "Entendiendo las comisiones en transacciones",
    category: "transactions",
    order: 4,
    keywords: ["comisiones", "costo", "cargos", "precios", "tasas"],
    content: `
# Comisiones de transacción

Desglose claro de todas las comisiones aplicadas a las transacciones.

## Tipos de comisiones

### Comisiones de conversión
- Aplicadas cuando el cripto se convierte a fiat
- Comisión de plataforma: 2.0% del monto de conversión
- Comisión de Bridge: ~0.1% del monto de conversión
- Total: ~2.1% del monto de conversión
- Todas las comisiones mostradas antes de confirmar
- Sin cargos ocultos

### Comisiones de red (Depósitos)
- Pagadas por el remitente (tú)
- Cubren costos de blockchain
- Varían según congestión de red
- No cobradas por Supabank

### Comisiones de pago
| Método | Comisión |
|--------|----------|
| ACH | $0 |
| Wire (EE.UU.) | $15 |
| Wire (Int'l) | $25 |
| SEPA | €0 |
| SPEI | $0 |

## Ejemplos de comisiones

### Ejemplo 1: Depósito de $1,000 USDC
- Depósito: $1,000.00
- Comisión plataforma (2%): -$20.00
- Comisión Bridge (~0.1%): -$1.00
- Pago (ACH): $0
- **Neto recibido: ~$979.00**

### Ejemplo 2: Depósito de 1 ETH ($3,000)
- Depósito: $3,000.00
- Comisión plataforma (2%): -$60.00
- Comisión Bridge (~0.1%): -$3.00
- Pago (Wire): -$15.00
- **Neto recibido: ~$2,922.00**

## Ver comisiones

### En la transacción
1. Abre los detalles de la transacción
2. Desplázate a la sección "Comisiones"
3. Ver desglose detallado

### En resumen
- Resumen mensual de comisiones en Ajustes
- Total de comisiones este mes
- Comparación con el anterior

## Transparencia de comisiones

Todas las comisiones son:
- Mostradas antes de confirmar
- Detalladas en la transacción
- Incluidas en estados de cuenta
- Nunca ocultas

## Descuentos por volumen

Los usuarios de alto volumen pueden calificar para:
- Comisiones de conversión reducidas
- Comisiones de pago exentas
- Precios personalizados
- Contacta con ventas para detalles
    `.trim(),
  },
  {
    id: "tx-005",
    slug: "refunds-policy",
    title: "Política de reembolsos",
    description: "Cómo funcionan los reembolsos en Supabank",
    category: "transactions",
    order: 5,
    keywords: ["reembolso", "devolución", "cancelar", "revertir", "política"],
    content: `
# Política de reembolsos

Entendiendo cuándo y cómo se procesan los reembolsos.

## Depósitos de cripto

### ¿Se pueden reembolsar los depósitos?
**Generalmente, no.** Las transacciones de cripto son:
- Irreversibles por diseño
- Convertidas automáticamente
- No pueden ser "devueltas"

### Excepciones
Los reembolsos pueden ser posibles si:
- El depósito aún no se ha convertido
- Error significativo del sistema
- Caso por caso
- Pueden aplicarse comisiones

## Pagos en fiat

### ¿Se pueden reembolsar los pagos?
Si el pago es devuelto por el banco:
- Fondos acreditados de vuelta al saldo
- Sin comisión adicional
- Puedes reintentar con datos correctos

### Pagos devueltos
Razones comunes:
- Datos bancarios incorrectos
- Cuenta cerrada
- Rechazo del banco
- Nombre no coincide

## Solicitar asistencia

### Para depósitos
Contacta con soporte con:
- Hash de transacción
- Monto enviado
- Descripción del problema
- Por qué se necesita reembolso

### Para pagos
Normalmente automático:
- Los pagos fallidos se devuelven automáticamente
- Verifica el estado de la transacción
- Contacta con soporte si no se devuelve en 5 días

## Tiempo de procesamiento

| Tipo | Plazo |
|------|-------|
| Pago devuelto | 1-3 días hábiles |
| Revisión de problema con depósito | 3-5 días hábiles |
| Casos especiales | Hasta 14 días |

## Notas importantes

⚠️ El cripto enviado a dirección incorrecta no se puede recuperar
⚠️ Las conversiones no se pueden revertir
⚠️ Las diferencias de tasa de mercado no son reembolsables
⚠️ Las comisiones de red no son reembolsables
    `.trim(),
  },
  {
    id: "tx-006",
    slug: "dispute-resolution",
    title: "Resolución de disputas",
    description: "Cómo resolver disputas de transacciones",
    category: "transactions",
    order: 6,
    keywords: ["disputa", "problema", "incidencia", "resolver", "reclamación"],
    content: `
# Resolución de disputas

Cómo manejar problemas y disputas de transacciones.

## Tipos de disputas

### Depósito faltante
- Enviado pero no recibido
- Monto incorrecto acreditado
- Retraso más allá de lo esperado

### Problemas de pago
- No recibido en el banco
- Monto incorrecto
- Enviado a cuenta incorrecta

### Disputas de conversión
- Tasa diferente a la esperada
- Desacuerdo con comisiones
- Error de cálculo

## Proceso de disputa

### Paso 1: Recopilar información
- ID de transacción
- Hash de transacción en blockchain
- Capturas de pantalla del problema
- Estados de cuenta bancarios (si aplica)

### Paso 2: Contactar con soporte
Envía correo a support@supabank.xyz con:
- Asunto: "Disputa - [ID de transacción]"
- Descripción clara
- Toda la documentación relevante

### Paso 3: Investigación
- Revisamos en 24 horas
- Podemos solicitar información adicional
- Verificamos todos los sistemas y registros

### Paso 4: Resolución
- Comunicamos los hallazgos
- Se toman acciones si corresponde
- Se proporciona explicación

## Plazos

| Etapa | Duración |
|-------|----------|
| Revisión inicial | 24 horas |
| Investigación | 3-5 días hábiles |
| Resolución | Dentro de 14 días |
| Apelaciones | 14 días adicionales |

## Qué investigamos

- Registros de blockchain
- Registros internos de transacciones
- Comunicación bancaria
- Marcas de tiempo del sistema
- Capturas de tasas

## Apelaciones

Si no estás de acuerdo con la resolución:
1. Responde al correo de disputa
2. Proporciona nueva información
3. Escalado a revisión superior
4. Se proporciona decisión final

## Prevención

Verifica todos los detalles antes de enviar
Usa las redes correctas
Mantén registros de transacciones
Reporta problemas rápidamente
    `.trim(),
  },
];
